import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  styled
} from '@mui/material'
import {
  CheckCircleOutline,
  CloseRounded,
  Visibility,
  VisibilityOff
} from '@mui/icons-material'

import { useAuthState } from '../Auth/AuthProvider'
import logo from '../../images/logo-wfgps.svg'
import {
  forgotPassword,
  resetPassword,
  verifyLoginCode
} from '../../services/AuthService'

const CustomTextField = styled(TextField)`
  width: 100%;
  .MuiOutlinedInput-root {
    border-radius: 8px;
  }
`

const STEPS = ['Email', 'Code', 'Password']

type ResetPasswordProps = {
  open: boolean
  onClose: () => void
  openLoginDialog: () => void
  openForgotPasswordDialog: () => void
  resetPasswordEmail?: string
}

export const ResetPassword = ({
  open,
  onClose,
  openLoginDialog,
  openForgotPasswordDialog,
  resetPasswordEmail
}: ResetPasswordProps) => {
  const [step, setStep] = useState(STEPS[0])

  const [email, setEmail] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [confirmEmailError, setConfirmEmailError] = useState('')
  const [codeError, setCodeError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')
  const [isProcessing, setProcessing] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)

  const { updateAuthState } = useAuthState()

  useEffect(() => {
    if (resetPasswordEmail) setStep(STEPS[1])
    else setStep(STEPS[0])

    setEmail(resetPasswordEmail || '')
  }, [resetPasswordEmail])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    switch (e.target.name) {
      case 'email':
        setEmail(e.target.value)
        emailValidator(e.target.value)
        break
      case 'confirmEmail':
        setConfirmEmail(e.target.value)
        confirmEmailValidator(e.target.value)
        break
      case 'code':
        setCode(e.target.value)
        codeValidator(e.target.value)
        break
      case 'password':
        setPassword(e.target.value)
        passwordValidator(e.target.value)
        break
      case 'confirmPassword':
        setConfirmPassword(e.target.value)
        confirmPasswordValidator(e.target.value)
        break
      default:
        break
    }
  }

  const handleBack = () => {
    switch (step) {
      case STEPS[0]:
        onClose()
        openLoginDialog()
        break
      case STEPS[1]:
        if (resetPasswordEmail) {
          onClose()
          openForgotPasswordDialog()
        } else {
          setStep(STEPS[0])
        }
        break
      default:
        setStep(STEPS[1])
    }
  }

  const handleNext = () => {
    switch (step) {
      case STEPS[0]:
        checkEmail()
        break
      case STEPS[1]:
        checkCode()
        break
      default:
        checkPassword()
    }
  }

  const emailValidator = (email: string) => {
    let message = ''

    if (!email.trim()) {
      message = 'Email is required'
    } else if (
      !/^[a-zA-Z0-9._]+@(?:[a-zA-Z0-9-]+\.)+[A-Za-z]+$/.test(email.trim())
    ) {
      message = 'Invalid email format'
    }

    setEmailError(message)
    return message
  }

  const confirmEmailValidator = (confirmEmail: string) => {
    const message =
      confirmEmail.trim() === email.trim() ? '' : 'Emails do not match'
    setConfirmEmailError(message)
    return message
  }

  const codeValidator = (code: string) => {
    const message = code.trim() ? '' : 'Code is required'
    setCodeError(message)
    return message
  }

  const passwordValidator = (password: string) => {
    const message = password.trim() ? '' : 'Password is required'
    setPasswordError(message)
    return message
  }

  const confirmPasswordValidator = (confirmPassword: string) => {
    const message = confirmPassword === password ? '' : 'Passwords do not match'
    setConfirmPasswordError(message)
    return message
  }

  const checkEmail = async () => {
    if (emailValidator(email)) return
    if (confirmEmailValidator(confirmEmail)) return

    setProcessing(true)

    const response = await forgotPassword(email.trim())
    if (response.data.forgotPassword.statusCode === 200) {
      setStep(STEPS[1])
    } else {
      setEmailError(response.data.forgotPassword.message)
    }

    setProcessing(false)
  }

  const checkCode = async () => {
    if (codeValidator(code)) return

    setProcessing(true)

    const response = await verifyLoginCode(email.trim(), code.trim())
    if (response.data.verifyLoginCode.statusCode === 200) {
      setStep(STEPS[2])
    } else {
      setCodeError(response.data.verifyLoginCode.message)
    }

    setProcessing(false)
  }

  const checkPassword = async () => {
    if (passwordValidator(password)) return
    if (confirmPasswordValidator(confirmPassword)) return

    setProcessing(true)

    const response = await resetPassword(email.trim(), password)
    if (response.data.resetPassword.statusCode === 200) {
      setShowSuccessMessage(true)
      localStorage.setItem(
        'user',
        JSON.stringify(response.data.resetPassword.data)
      )
      updateAuthState(true)
    } else {
      setCodeError(response.data.resetPassword.message)
    }

    setProcessing(false)
  }

  const closeSuccessDialog = () => {
    setShowSuccessMessage(false)
    onClose()
    window.location.reload()
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        sx={{
          '.MuiDialog-paper': {
            height: { xs: 'calc(100% - 56px)', sm: 'fit-content' },
            borderRadius: 3,
            display: 'inline-table',
            maxHeight: '100%',
            mt: 11
          }
        }}
      >
        <Box sx={{ width: { xs: '100vw', sm: 700, md: 800 }, display: 'flex' }}>
          <Box
            sx={{
              width: 247,
              alignItems: 'center',
              bgcolor: 'primary.main',
              borderRadius: 3,
              display: { xs: 'none', sm: 'flex' },
              justifyContent: 'center',
              m: 1,
              mr: 0
            }}
          >
            <Box component='img' src={logo} />
          </Box>

          <Box sx={{ flex: 1, position: 'relative' }}>
            <CloseRounded
              onClick={onClose}
              sx={{
                color: '#333',
                cursor: 'pointer',
                fontSize: 20,
                position: 'absolute',
                right: 17,
                top: 12
              }}
            />

            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                maxWidth: { xs: 300, sm: 334 },
                mx: 'auto'
              }}
            >
              <Typography
                sx={{ bgcolor: '#FFF5EB', borderRadius: 3, color: 'black', fontSize: 12, fontWeight: 500, mt: 5, mb: 1.5, px: 2, py: 2.5, whiteSpace: { md: 'nowrap' } }}
                variant='body1'
                align='left'
                color='primary'
              >
                <b>Note:</b> Do not close this window until each step has been completed.
              </Typography>

              <Typography
                color='primary'
                sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1 }}
              >
                Reset Password
              </Typography>

              <Typography
                color='primary'
                sx={{
                  letterSpacing: 0.1,
                  lineHeight: 1.25,
                  mb: 1.75,
                  mt: 1.5,
                  textAlign: 'center'
                }}
              >
                {step === STEPS[0]
                  ? 'Please type your email address below to receive a temporary log in code.'
                  : step === STEPS[1]
                  ? 'Please enter your temporary log in code below'
                  : 'Please enter your new password below'}
              </Typography>

              {step === STEPS[0] && (
                <>
                  <CustomTextField
                    size='small'
                    label='Email'
                    name='email'
                    type='email'
                    value={email}
                    onChange={handleChange}
                    error={!!emailError}
                    helperText={emailError}
                    InputLabelProps={{ shrink: true }}
                  />

                  <CustomTextField
                    size='small'
                    label='Confirm Email'
                    name='confirmEmail'
                    type='email'
                    value={confirmEmail}
                    onChange={handleChange}
                    error={!!confirmEmailError}
                    helperText={confirmEmailError}
                    InputLabelProps={{ shrink: true }}
                  />
                </>
              )}

              {step === STEPS[1] && (
                <CustomTextField
                  size='small'
                  label='Temporary Log In Code'
                  name='code'
                  type='text'
                  value={code}
                  onChange={handleChange}
                  error={!!codeError}
                  helperText={codeError}
                  InputLabelProps={{ shrink: true }}
                />
              )}

              {step === STEPS[2] && (
                <>
                  <CustomTextField
                    size='small'
                    label='Password'
                    name='password'
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={handleChange}
                    error={!!passwordError}
                    helperText={passwordError}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton edge='end' onClick={() => setShowPassword(!showPassword)} size="large">
                            {showPassword ? (
                              <VisibilityOff sx={{ fontSize: 24 }} />
                            ) : (
                              <Visibility sx={{ fontSize: 24 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <CustomTextField
                    size='small'
                    label='Retype Password'
                    name='confirmPassword'
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={handleChange}
                    error={!!confirmPasswordError}
                    helperText={confirmPasswordError}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            edge='end'
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            size="large">
                            {showConfirmPassword ? (
                              <VisibilityOff sx={{ fontSize: 24 }} />
                            ) : (
                              <Visibility sx={{ fontSize: 24 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </>
              )}

              <Button
                variant='contained'
                disabled={isProcessing}
                onClick={handleNext}
                sx={{
                  width: '100%',
                  borderRadius: 16,
                  fontSize: 14,
                  lineHeight: '20px',
                  mt: 2,
                  p: 1.25,
                  textTransform: 'none'
                }}
              >
                {step === STEPS[2] ? 'Apply' : 'Next'}
              </Button>

              <Button
                variant='outlined'
                onClick={handleBack}
                sx={{
                  width: '100%',
                  height: 40,
                  borderRadius: 16,
                  fontSize: 14,
                  lineHeight: '20px',
                  mb: 4,
                  mt: 2,
                  p: 1.25,
                  textTransform: 'none'
                }}
              >
                Back
              </Button>
            <Grid
              sx={{
                pb: 4,
                display: 'flex',
                flexDirection: {
                  sm: 'column-reverse',
                  md: 'row',
                  xs: 'column-reverse'
                }
              }}
            >
              <Grid
                item
                sm={12}
                md={12}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 400 }}>
                  Having issues?
                </Typography>
                <Link
                  href='mailto:events@workforcegps.org'
                  color='secondary'
                  underline='none'
                  sx={{
                    display: 'inline',
                    ml: 1,
                    fontSize: 12,
                    fontWeight: 600
                  }}
                >
                  Contact Support
                </Link>
              </Grid>
            </Grid>
            </Box>
          </Box>
        </Box>
      </Dialog>
      <Dialog
        open={showSuccessMessage}
        onClose={closeSuccessDialog}
        sx={{ '.MuiDialog-paper': { borderRadius: 3 } }}
      >
        <Box
          sx={{
            width: { sm: 545 },
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 3, sm: 0 },
            position: 'relative'
          }}
        >
          <CloseRounded
            onClick={closeSuccessDialog}
            sx={{
              color: '#333',
              cursor: 'pointer',
              fontSize: 20,
              position: 'absolute',
              right: 17,
              top: 12
            }}
          />

          <Box sx={{ alignItems: 'center', display: 'flex', mt: 7.5 }}>
            <CheckCircleOutline sx={{ color: '#006BD7', fontSize: 64 }} />

            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 500,
                lineHeight: 1.4,
                maxWidth: 250,
                ml: 2
              }}
            >
              Great! Your password has been successfully reset.
            </Typography>
          </Box>

          <Button
            variant='contained'
            onClick={closeSuccessDialog}
            sx={{
              width: { xs: '100%', sm: 334 },
              borderRadius: 16,
              fontSize: 14,
              lineHeight: '20px',
              mt: 6,
              mb: 5,
              p: 1.25,
              textTransform: 'none'
            }}
          >
            Continue
          </Button>
        </Box>
      </Dialog>
    </>
  );
}
